import { getItem, rarityRank, } from '../data/items.js';
import { isWardrobeSlot } from './inventory.js';
/**
 * The bag's « Ranger » button: one plan that tidies the 30 slots. Pure, so
 * the server applies it inside a transaction and the tests replay it.
 *
 * Order: equipment first (what a player compares and wears), then
 * consumables (what he reaches for in a fight), then materials (what waits
 * for the smith, the alchemist or a quest); inside each, the rarest first,
 * then the highest level required, then by template, then the smith's best
 * work first. A template the game no longer knows goes last.
 *
 * Stacks of one template merge up to its `maxStack`, but only when nothing
 * tells their rows apart (`interchangeable`): a smith's level, affixes, the
 * bound flag or a purchase's trade cooldown each make a row its own.
 * Wardrobe rows (ADR 0072) are neither read nor moved.
 */
const KIND_ORDER = ['equipment', 'consumable', 'material'];
/** Whether two rows may become one stack: same template, nothing of their own. */
export function interchangeable(a, b) {
    return (a.templateId === b.templateId &&
        plain(a) &&
        plain(b) &&
        a.bound === b.bound &&
        (a.tradableAfter ?? null) === (b.tradableAfter ?? null));
}
function plain(item) {
    return item.upgradeLevel === 0 && (item.affixes === undefined || item.affixes.length === 0);
}
/** What separates the merge groups of one template. */
function groupKey(item) {
    return [item.templateId, String(item.bound), item.tradableAfter ?? ''].join('|');
}
function compareItems(a, b, templateOf) {
    const ta = templateOf(a.templateId);
    const tb = templateOf(b.templateId);
    if (ta === undefined || tb === undefined) {
        if (ta !== tb)
            return ta === undefined ? 1 : -1;
    }
    else {
        const byKind = KIND_ORDER.indexOf(ta.kind) - KIND_ORDER.indexOf(tb.kind);
        if (byKind !== 0)
            return byKind;
        const byRarity = rarityRank(tb.rarity) - rarityRank(ta.rarity);
        if (byRarity !== 0)
            return byRarity;
        const byLevel = tb.levelRequired - ta.levelRequired;
        if (byLevel !== 0)
            return byLevel;
    }
    if (a.templateId !== b.templateId)
        return a.templateId < b.templateId ? -1 : 1;
    const byUpgrade = b.upgradeLevel - a.upgradeLevel;
    if (byUpgrade !== 0)
        return byUpgrade;
    // Rows of one template that cannot merge keep a fixed order: the plain
    // ones before the bound, the tradable before the cooling down.
    if (a.bound !== b.bound)
        return a.bound ? 1 : -1;
    const coolA = a.tradableAfter ?? '';
    const coolB = b.tradableAfter ?? '';
    if (coolA !== coolB)
        return coolA < coolB ? -1 : 1;
    const byQuantity = b.quantity - a.quantity;
    if (byQuantity !== 0)
        return byQuantity;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}
/**
 * Pours each group of interchangeable rows into as few stacks as its
 * `maxStack` allows. The biggest rows (then the smallest id) keep the
 * stacks, so a bag already merged changes nothing.
 */
function merge(items, templateOf) {
    const groups = new Map();
    const kept = [];
    for (const item of items) {
        const template = templateOf(item.templateId);
        if (template === undefined || template.maxStack <= 1 || !plain(item)) {
            kept.push(item);
            continue;
        }
        const key = groupKey(item);
        groups.set(key, [...(groups.get(key) ?? []), item]);
    }
    const removed = [];
    for (const group of groups.values()) {
        const first = group[0];
        const maxStack = first === undefined ? 1 : (templateOf(first.templateId)?.maxStack ?? 1);
        const rows = [...group].sort((a, b) => b.quantity - a.quantity || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
        let total = rows.reduce((sum, row) => sum + row.quantity, 0);
        for (const row of rows) {
            if (total <= 0) {
                removed.push(row.id);
                continue;
            }
            const quantity = Math.min(maxStack, total);
            total -= quantity;
            kept.push(quantity === row.quantity ? row : { ...row, quantity });
        }
    }
    return { kept, removed };
}
/**
 * The bag tidied: rows merged where they may, then laid from slot 0 in the
 * order above. `bag` may hold wardrobe entries too; they are left out of the
 * result and never touched. Sorting a sorted bag returns it unchanged.
 */
export function sortBag(bag, templateOf = getItem) {
    const inBag = bag.filter((entry) => !isWardrobeSlot(entry.slot)).map((entry) => entry.item);
    const { kept, removed } = merge(inBag, templateOf);
    const ordered = [...kept].sort((a, b) => compareItems(a, b, templateOf));
    return {
        bag: ordered.map((item, slot) => ({ slot, item })),
        removed: removed.sort(),
    };
}
//# sourceMappingURL=bag-sort.js.map